// @flow
import type { Sample, DataField, TestSchedule, Product, DataFieldName } from "../../lib/Types";
import React, { Fragment } from "react";
import ShoppingCart from "./ShoppingCart";
import BasketData from "../../lib/BasketData";
import styled, { css } from "styled-components";
import TextInput from "./inputs/TextInput";
import NumberInput from "./inputs/NumberInput";
import Button from "../Button";
import FixedDropDown from "./inputs/FixedDropDown";
import { AngleDoubleDown } from "styled-icons/fa-solid/AngleDoubleDown";
import SampleStore from "../../lib/SampleStore";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import { DefaultContainer } from "./Theme";
import TurnaroundPremiumSetInput from "./inputs/TurnaroundPremiumSetInput";
import RequestedTestDate from "./inputs/RequestedTestDate";
import Link from "../Link";
import Expander from "../Expander";
import AdditionalTestingSelector from "./AdditionalTestingSelector";
import ExistingSampleQuotables from "./ExistingSampleQuotables";
import RegistrationModal from "./RegistrationModal";
import CloneSample from "./CloneSample";
import ProtocolRegistration from "./ProtocolRegistration";

const Tr = styled.tr`
  ${(props) =>
    props.valid &&
    css`
      background-color: #9bff9b;
    `};
  > td {
    white-space: nowrap;
    border-top: 1px solid #eee;
    border-right: 1px solid #eee;
    ${(props) =>
      props &&
      props.selected &&
      css`
        border-top: 1px solid #ccc;
        border-bottom: 1px solid #ccc;
        background-color: #f5f5f5;
      `} ${(props) =>
      props &&
      props.selected &&
      props.valid &&
      css`
        background-color: #caffca;
      `};
  }
`;
const Td = styled.td`
  padding-left: 10px;
  padding-right: 15px;
  font-size: 8pt;
`;
const FieldTd = styled.td`
  ${(props) =>
    props.empty &&
    css`
      background: repeating-linear-gradient(
        45deg,
        #fafafa,
        #fafafa 10px,
        #efefef 10px,
        #efefef 20px
      );
    `};
`;
const ProductName = styled.h2`
  font-size: 14pt;
  margin-top: 0px;
`;

const MatrixName = styled.span`
  color: #888;
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14pt;
  margin-bottom: 10px;
`;

const ClientSuiteName = styled.h2`
  font-size: 12pt;
`;
const FieldContainer = styled.div`
  min-width: 450px;
  border-bottom: 1px dotted #dadada;
  margin-right: 10px;
  font-size: 10pt;
  display: flex;
  justify-content: flex-start;
  flex-grow: 1;
  input,
  select {
    border-radius: 5px;
    border: 1px solid #ddd;
    background-color: white;
  }
`;
const SampleContainer = styled(DefaultContainer)`
  padding: 20px;
  margin-bottom: 20px;
  background-color: white;
`;
const FieldLabel = styled.div`
  font-size: 10pt;
  padding-left: 5px;
  padding-top: 4px;
  min-width: 200px;
`;
const FixedValue = styled.span`
  padding-right: 10px;
  padding-left: 10px;
`;
const CopyDownArrow = styled(AngleDoubleDown)`
  margin-left: 5px;
  margin-right: 5px;
  cursor: pointer;
`;

type Props = {
  sample: Sample,
  dataFieldNames: ?Array<DataFieldName>,
  store: SampleStore,
  mode: string,
  showTurnarounds: boolean,
  focusSample: Function,
  opened: boolean,
  openSample: Function,
  openNextSample: Function,
  nextPreviousSample: Function,
  selected: boolean,
  onFocus: Function,
  showMatrix: boolean,
  showClientSuite: boolean,
  companyId: number,
};

class CopyDownWrapper extends React.Component {
  render() {
    const { copyDown } = this.props;
    return (
      <Fragment>
        <div style={{ display: "flex", alignItems: "center" }}>
          {this.props.children}
          <CopyDownArrow size={15} onClick={copyDown} />
        </div>
      </Fragment>
    );
  }
}

class SampleForm extends React.Component<Props> {
  state = {
    showTesting: false,
    cloneCount: 0,
  };

  toggleTesting = () => {
    this.props.openSample(this.props.sample);
    // this.setState({ showTesting: !this.state.showTesting });
  };

  isSingleColumn = () => this.props.mode == "single_column";

  onFocus = () => {
    this.props.focusSample(this.props.sample);
  };

  gridColForDataField = (dataField: DataField, variant: ?string) => {
    const id = dataField && dataField.data_field_name && dataField.data_field_name.id;
    if (!id) return null;
    if (variant) return `data_field:${id}:${variant}`;
    return `data_field:${id}`;
  };

  createInputType = (dataField: DataField, copyDown: ?Function) => {
    const { store } = this.props;
    if (dataField.type == "free_text") {
      return (
        <TextInput
          data_field={dataField}
          sample={this.props.sample}
          fullWidth={this.isSingleColumn()}
          onFocus={this.onFocus}
          onCtrlDown={copyDown}
          gridCol={this.gridColForDataField(dataField)}
        />
      );
    }
    if (dataField.type == "number") {
      return (
        <NumberInput
          data_field={dataField}
          fullWidth={this.isSingleColumn()}
          onFocus={this.onFocus}
          onCtrlDown={copyDown}
          gridCol={this.gridColForDataField(dataField)}
        />
      );
    }
    if (dataField.type == "fixed_drop_down") {
      return (
        <FixedDropDown
          data_field={dataField}
          fullWidth={this.isSingleColumn()}
          onFocus={this.onFocus}
          onCtrlDown={copyDown}
          gridCol={this.gridColForDataField(dataField)}
        />
      );
    }
    if (dataField.type == "ammendable_drop_down") {
      return (
        <Fragment>
          <FixedDropDown
            data_field={dataField}
            fullWidth={this.isSingleColumn()}
            onFocus={this.onFocus}
            onCtrlDown={copyDown}
            gridCol={this.gridColForDataField(dataField, "select")}
          />
          <TextInput
            data_field={dataField}
            sample={this.props.sample}
            fullWidth={this.isSingleColumn()}
            placeholder="..or add new"
            onFocus={this.onFocus}
            onCtrlDown={copyDown}
            gridCol={this.gridColForDataField(dataField, "text")}
          />
        </Fragment>
      );
    }
    if (
      ["fixed_value_from_drop_down", "fixed_value_from_drop_down_per_product"].includes(
        dataField.type
      )
    ) {
      return <FixedValue>{dataField.value}</FixedValue>;
    }

    return null;
  };

  renderTitle = (sample) => {
    const { store, openPreviousSample, openNextSample } = this.props;
    const { cloneCount } = this.state;

    return (
      <div>
        <Title>
          <div style={{ width: 70 }}>
            <span className="label label-success">{store.sequencePosition(sample)}</span>{" "}
          </div>
          <div style={{ width: "100%", paddingLeft: 10 }}>
            {sample.product.name && (
              <span>
                {sample.product.name}
                {sample.schedule.client_suite_name &&
                  " - " + sample.schedule.client_suite_name}
              </span>
            )}
            {sample.product.name && sample.matrix_name && <span>&nbsp;-&nbsp;</span>}
            {sample.matrix_name && <MatrixName>{sample.matrix_name}</MatrixName>}
          </div>

          <div className="pull-right">
            <CloneSample store={store} sample={sample} />
          </div>
        </Title>
        <p>
          <Link onClick={openPreviousSample}>Previous Sample</Link>
          &nbsp;|&nbsp;
          <Link onClick={openNextSample}>Next Sample</Link>
        </p>
      </div>
    );
  };

  generateSampleField = (dataFieldName: DataFieldName, includeCopyDown = true) => {
    const { sample, store } = this.props;
    const dataField = sample.data_fields.find(
      (df) => df.data_field_name.id == dataFieldName.id
    );
    if (!dataField) return null;

    if (
      ["fixed_value_from_drop_down", "fixed_value_from_drop_down_per_product"].includes(
        dataField.type
      ) ||
      !includeCopyDown
    ) {
      return this.createInputType(dataField);
    }
    const copyDown = () => store.copyDown(sample, dataFieldName);
    return (
      <CopyDownWrapper copyDown={copyDown}>
        {this.createInputType(dataField, copyDown)}
      </CopyDownWrapper>
    );
  };

  renderProtocol({ sample, colSpan }) {
    console.log(sample);
    const protocol = sample.protocol;
    if (!protocol.testing_points) return null;
    return (
      <tr>
        <td colSpan={colSpan} style={{ backgroundColor: "rgb(234 240 249)" }}>
          <ProtocolRegistration sample={sample} forceUpdate={() => this.forceUpdate()} />
        </td>
      </tr>
    );
  }

  renderTabular() {
    const {
      sample,
      dataFieldNames,
      store,
      showTurnarounds,
      selected,
      showClientSuite,
      showMatrix,
      opened,
      companyId,
    } = this.props;
    // const { showTesting } = this.state;
    const showTesting = opened;
    const colSpan = 6 + (showTurnarounds ? 1 : 0) + dataFieldNames.length;
    return (
      <Fragment>
        <Tr valid={store.isSampleValid(sample)} selected={selected} data-sample-grid-row="true">
          <Td>
            <Link onClick={() => store.removeSample(sample)} style={{ color: "red" }}>
              <i className="fa fa-trash" />
            </Link>
          </Td>
          <td
            style={{
              backgroundColor: showTesting ? "rgb(90, 192, 173)" : null,
              paddingRight: 5,
            }}
          >
            <Link
              onClick={() => this.toggleTesting()}
              style={{ color: showTesting ? "white" : null }}
            >
              <Expander expanded={showTesting} />
              Edit &nbsp;
              {sample.quote_item_ids.length > 0 && (
                <span className="badge">+ {sample.quote_item_ids.length}</span>
              )}
            </Link>
          </td>
          <Td className="product-name">{sample.product.name}</Td>
          {showMatrix && <Td>{sample.matrix_name}</Td>}
          {showClientSuite && (
            <Td>
              {sample.protocol ? (
                "-"
              ) : (
                <Link onClick={() => this.toggleTesting()}>
                  {sample.schedule.client_suite_name}
                </Link>
              )}
            </Td>
          )}
          <FieldTd>
            {!sample.id && sample.protocol ? (
              "-"
            ) : sample.lock_requested_test_date ? (
              sample.requested_test_date
            ) : (
              <RequestedTestDate
                sample={sample}
                onFocus={this.onFocus}
                forceUpdate={() => this.forceUpdate()}
                copyDown={() => store.copyDownRequestedTestDate(sample)}
                gridCol="requested_test_date"
              />
            )}
          </FieldTd>
          {showTurnarounds && (
            <Td>
              <TurnaroundPremiumSetInput
                sample={sample}
                turnaroundPremiumSet={sample.turnaround_premium_set}
                onFocus={this.onFocus}
                gridCol="turnaround"
              />
            </Td>
          )}
          {dataFieldNames.map((dataFieldName) => {
            var component = this.generateSampleField(dataFieldName);
            return (
              <FieldTd key={dataFieldName.id} empty={component == null}>
                {component}
              </FieldTd>
            );
          })}
        </Tr>
        {showTesting &&
          sample.id && (
            <RegistrationModal
              show={true}
              onClose={this.toggleTesting}
              style={{ maxWidth: 1200, paddingTop: 20 }}
            >
              {this.renderTitle(sample)}
              <ExistingSampleQuotables
                sampleId={sample.id}
                onWithdrawal={(withdrawing) => sample.withdrawing.replace(withdrawing)}
                withdrawing={sample.withdrawing}
              />
              <AdditionalTestingSelector
                sampleId={sample.id}
                companyId={sample.company_id}
                quote_item_ids={toJS(sample.quote_item_ids)}
                onChangeQuoteItems={(ids) => sample.quote_item_ids.replace(ids)}
                companyId={companyId}
              />
            </RegistrationModal>
          )}
        {showTesting &&
          !sample.id && (
            <RegistrationModal
              show={true}
              onClose={this.toggleTesting}
              style={{ maxWidth: 1200 }}
              style={{ maxWidth: 1200, paddingTop: 20 }}
            >
              {this.renderTitle(sample)}
              <div className="" style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  <FieldContainer>
                    <FieldLabel>Requested Test Date</FieldLabel>
                    <RequestedTestDate
                      sample={sample}
                      onFocus={this.onFocus}
                      forceUpdate={() => this.forceUpdate()}
                      copyDown={() => store.copyDownRequestedTestDate(sample)}
                      gridCol="requested_test_date"
                    />
                  </FieldContainer>
                  {showTurnarounds && (
                    <FieldContainer>
                      <FieldLabel>Turnaround</FieldLabel>
                      <TurnaroundPremiumSetInput
                        sample={sample}
                        turnaroundPremiumSet={sample.turnaround_premium_set}
                        onFocus={this.onFocus}
                        gridCol="turnaround"
                      />
                    </FieldContainer>
                  )}
                  {sample.data_fields.map((dataField) => (
                    <FieldContainer>
                      <FieldLabel>{dataField.data_field_name.name}</FieldLabel>
                      {this.generateSampleField(dataField.data_field_name, false)}
                    </FieldContainer>
                  ))}
                </div>
              </div>
              {!sample.protocol && (
                <AdditionalTestingSelector
                  companyId={sample.company_id}
                  clientSpecificationId={sample.schedule.client_specification_id}
                  clientSuiteId={sample.schedule.client_suite_id}
                  matrixId={sample.schedule.matrix_id}
                  quote_item_ids={toJS(sample.quote_item_ids)}
                  onChangeQuoteItems={(ids) => sample.quote_item_ids.replace(ids)}
                  companyId={companyId}
                />
              )}
            </RegistrationModal>
          )}
        {sample.protocol && this.renderProtocol({ sample, colSpan })}
      </Fragment>
    );
  }

  // renderSingleColumn() {
  //   const { sample, store } = this.props;
  //   return (
  //     <SampleContainer valid={store.isSampleValid(sample)}>
  //       <ProductName>
  //         {this.renderCheckbox(sample)}
  //         &nbsp;&nbsp;&nbsp;&nbsp;
  //         {sample.product.name}
  //       </ProductName>
  //       <ClientSuiteName>{sample.schedule.client_suite_name}</ClientSuiteName>
  //       {sample.data_fields.map(dataField => (
  //         <FieldContainer>
  //           <FieldLabel>{dataField.data_field_name.name}</FieldLabel>
  //           {this.generateSampleField(dataField.data_field_name)}
  //         </FieldContainer>
  //       ))}
  //     </SampleContainer>
  //   );
  // }

  render() {
    return this.renderTabular();
  }
}

export default observer(SampleForm);
